import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext, subscribeDebug, getDebugLogs, clearDebugLogs } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';

const DebugConsole = () => {
  const [logs, setLogs] = useState(getDebugLogs());
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const unsub = subscribeDebug((entry) => {
      if (entry.level === 'clear') setLogs([]);
      else setLogs(prev => [...prev, entry]);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [logs]);

  const levelColors = { info: 'text-blue-400', warn: 'text-yellow-400', error: 'text-red-400', success: 'text-green-400' };
  const levelIcons = { info: 'ℹ️', warn: '⚠️', error: '❌', success: '✅' };

  if (!isOpen) {
    return (
      <button onClick={() => setIsOpen(true)} className="fixed bottom-4 right-4 z-50 bg-gray-900 text-white text-xs font-mono px-3 py-2 rounded-lg shadow-lg border border-gray-700 hover:bg-gray-800 transition-colors flex items-center gap-2">
        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
        Debug ({logs.length})
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-950 border-t border-gray-700 shadow-2xl" style={{ height: '45vh' }}>
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <span className="text-white text-xs font-mono font-bold">Auth Debug</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={clearDebugLogs} className="text-gray-400 hover:text-white text-xs font-mono px-2 py-1 rounded bg-gray-800">Clear</button>
          <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white text-xs font-mono px-2 py-1 rounded bg-gray-800">Min</button>
        </div>
      </div>
      <div ref={scrollRef} className="overflow-y-auto p-3 font-mono text-xs" style={{ height: 'calc(45vh - 40px)' }}>
        {logs.map((log) => (
          <div key={log.id} className="mb-1.5 leading-relaxed">
            <span className="text-gray-600">{log.timestamp}</span>{' '}
            <span className={levelColors[log.level] || 'text-gray-300'}>{levelIcons[log.level] || '•'} [{log.level?.toUpperCase()}]</span>{' '}
            <span className="text-gray-200">{log.message}</span>
            {log.data && <pre className="mt-1 ml-4 text-gray-500 whitespace-pre-wrap break-all text-[10px]">{log.data}</pre>}
          </div>
        ))}
      </div>
    </div>
  );
};

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup } = useContext(AuthContext);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const inviteToken = searchParams.get('invite');
  const inviteInfo = searchParams.get('info');

  let inviteData = null;
  if (inviteInfo) {
    try { inviteData = JSON.parse(decodeURIComponent(inviteInfo)); } catch { inviteData = null; }
  }

  useEffect(() => {
    if (inviteToken) {
      localStorage.setItem('pendingInviteToken', inviteToken);
    }
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');
    setErrorType(null);

    if (!form.name || !form.email || !form.password) {
      setError('Please fill in all fields');
      setErrorType('generic');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      setErrorType('generic');
      return;
    }

    if (inviteToken) {
      localStorage.setItem('pendingInviteToken', inviteToken);
    }

    setLoading(true);
    const result = await signup(form.name, form.email, form.password);
    setLoading(false);

    if (result.success) {
      if (result.needsEmailConfirmation) {
        setSignupSuccess(true);
        return;
      }
      const pending = localStorage.getItem('pendingInviteToken');
      if (pending) {
        localStorage.removeItem('pendingInviteToken');
        navigate(`/invite/${pending}`, { replace: true });
      } else {
        navigate('/home', { replace: true });
      }
    } else {
      if (result.isRateLimited) setErrorType('ratelimit');
      else if (result.isAlreadyRegistered) setErrorType('exists');
      else if (result.isNetworkError || result.isTimeout) setErrorType('network');
      else setErrorType('generic');
      setError(result.error || 'Something went wrong');
    }
  };

  const handleTryLogin = () => {
    const pending = localStorage.getItem('pendingInviteToken');
    navigate(pending ? `/login?invite=${pending}` : '/login');
  };

  if (signupSuccess) {
    return (
      <div className="mobile-container bg-white flex flex-col">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex-1 flex flex-col items-center justify-center px-6">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.1 }} className="w-24 h-24 bg-gradient-to-br from-pink-100 to-rose-100 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-pink-100">
            <SafeIcon name="Mail" className="text-pink-500 w-12 h-12" />
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="text-2xl font-bold text-gray-900 mb-3 text-center">Check your email! 📧</motion.h1>
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="text-gray-500 text-center mb-6 max-w-xs leading-relaxed">
            We sent a <strong className="text-gray-700">confirmation link</strong> to{' '}
            <strong className="text-pink-600">{form.email}</strong>. Click the link to confirm your account.
          </motion.p>
          {inviteToken && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }} className="bg-pink-50 border border-pink-200 rounded-2xl p-4 mb-6 w-full max-w-xs text-center">
              <p className="text-sm text-pink-700">After confirming, you'll be able to respond to your invitation! 💌</p>
            </motion.div>
          )}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 w-full max-w-xs">
            <div className="flex items-start gap-3">
              <SafeIcon name="Info" className="text-amber-500 w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 mb-1">Don't see the email?</p>
                <p className="text-xs text-amber-700 leading-relaxed">Check your spam or junk folder.</p>
              </div>
            </div>
          </motion.div>
          <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} onClick={handleTryLogin} className="w-full max-w-xs bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200">
            Go to Login
          </motion.button>
        </motion.div>
        <DebugConsole />
      </div>
    );
  }

  return (
    <div className="mobile-container bg-white flex flex-col">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex-1 flex flex-col px-6 pt-12 pb-8">
        <button onClick={() => navigate('/')} className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mb-8">
          <SafeIcon name="ArrowLeft" className="text-gray-600" />
        </button>
        <div className="mb-8">
          <img src="https://cloud.sharemyimage.com/2026/08/16/App-Icon.png" alt="DayClap" className="w-36 h-36 rounded-3xl mb-5 shadow-sm" />
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Create your account</h1>
          <p className="text-gray-500">Start creating something special today. ❤️</p>
        </div>
        {inviteToken && inviteData && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-pink-50 border border-pink-200 rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <SafeIcon name="Heart" className="text-pink-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-pink-800">{inviteData.sender} sent you an invitation!</p>
                <p className="text-xs text-pink-600 mt-0.5">Sign up to respond to their {inviteData.occasion}</p>
              </div>
            </div>
          </motion.div>
        )}
        {inviteToken && !inviteData && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-pink-50 border border-pink-200 rounded-2xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <SafeIcon name="Mail" className="text-pink-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-pink-800">You have a pending invitation!</p>
                <p className="text-xs text-pink-600 mt-0.5">Sign up to view and respond</p>
              </div>
            </div>
          </motion.div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Your Name</label>
            <div className="relative">
              <SafeIcon name="User" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="What should we call you?" className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none transition-all" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
            <div className="relative">
              <SafeIcon name="Mail" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="w-full pl-12 pr-4 py-4 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none transition-all" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
            <div className="relative">
              <SafeIcon name="Lock" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="w-full pl-12 pr-12 py-4 rounded-xl border border-gray-200 focus:border-pink-500 focus:ring-2 focus:ring-pink-100 outline-none transition-all" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                <SafeIcon name={showPassword ? 'EyeOff' : 'Eye'} />
              </button>
            </div>
          </div>
          <AnimatePresence>
            {errorType === 'generic' && error && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-red-50 border border-red-100 text-red-600 text-sm p-3 rounded-xl flex items-center gap-2">
                <SafeIcon name="AlertCircle" />{error}
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {errorType === 'ratelimit' && error && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-amber-50 border border-amber-100 text-amber-700 text-sm p-3 rounded-xl flex items-center gap-2">
                <SafeIcon name="Clock" />{error}
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {errorType === 'exists' && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon name="Info" className="text-blue-500" />
                  <p className="text-sm font-semibold text-blue-800">Account already exists</p>
                </div>
                <p className="text-xs text-blue-700 mb-3">This email is already registered.</p>
                <button type="button" onClick={handleTryLogin} className="w-full bg-blue-600 text-white font-semibold py-2.5 rounded-lg text-sm">Go to Login</button>
              </motion.div>
            )}
          </AnimatePresence>
          <button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200 disabled:opacity-60 flex items-center justify-center gap-2 mt-6">
            {loading ? (<><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Creating account...</>) : inviteToken ? 'Sign Up & Respond ❤️' : 'Create Account ❤️'}
          </button>
        </form>
        <p className="text-center text-sm text-gray-500 mt-6">
          Already have an account?{' '}
          <Link to={inviteToken ? `/login?invite=${inviteToken}&info=${inviteInfo || ''}` : '/login'} className="text-pink-500 font-semibold">Sign in</Link>
        </p>
      </motion.div>
      <DebugConsole />
    </div>
  );
};

export default Signup;